(() => {
  const dispatch = (name, type) => {
    const detail = { type };
    document.dispatchEvent(new CustomEvent(name, { detail }));
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish === 'function') {
      try {
        Promise.resolve(publish.call(window.Shopify.analytics, name, detail)).catch(() => {});
      } catch {}
    }
  };
  const completionTimers = new WeakMap();

  const getLanguageError = (value, configuredLanguages) => {
    if (!value || !configuredLanguages) return '';
    const languages = configuredLanguages.toLowerCase();
    const allowsHebrew = languages.includes('עברית') || languages.includes('hebrew');
    const allowsEnglish = languages.includes('אנגלית') || languages.includes('english');
    if (allowsHebrew === allowsEnglish) return '';
    const invalidHebrew = allowsHebrew && /[^\p{Script=Hebrew}\p{N}\p{P}\p{Z}\p{M}]/u.test(value);
    const invalidEnglish = allowsEnglish && /[^\p{Script=Latin}\p{N}\p{P}\p{Z}\p{M}]/u.test(value);
    return invalidHebrew || invalidEnglish ? `יש להזין טקסט ב${allowsHebrew ? 'עברית' : 'אנגלית'} בלבד` : '';
  };

  const getFormId = (form) => form?.getAttribute('id') || '';

  const getErrorSummary = (form) => Array.from(document.querySelectorAll('[data-zoria-personalization-errors]'))
    .find((summary) => summary.dataset.formId === getFormId(form));

  const getInvalidPersonalizationFields = (form) => Array.from(form?.elements || [])
    .filter((control) =>
      control.matches?.('[data-zoria-personalization-input], .zoria-file-upload input[type="file"]')
      && !control.validity.valid
    );

  const getFieldLabel = (control) => {
    const labelId = (control.getAttribute('aria-labelledby') || '').split(/\s+/).find(Boolean);
    return document.getElementById(labelId)?.textContent.trim()
      || control.labels?.[0]?.textContent.trim()
      || control.name;
  };

  const getFocusTarget = (control) => control.matches?.('.zoria-file-upload input[type="file"]')
    ? control.closest('.zoria-file-upload')?.querySelector('.zoria-file-upload__picker') || control
    : control;

  const updateErrorSummary = (form, focusFirst = false) => {
    const summary = getErrorSummary(form);
    if (!summary) return;
    const list = summary.querySelector('ul');
    const invalidFields = getInvalidPersonalizationFields(form);
    list.replaceChildren();
    invalidFields.forEach((control) => {
      const focusTarget = getFocusTarget(control);
      const fieldError = control.closest('.custom-product-field')?.querySelector('.input-error-msg');
      const fieldErrorVisible = fieldError
        && (fieldError.dataset.visible === 'true' || (control.type === 'file' && !fieldError.hidden));
      const message = (fieldErrorVisible ? fieldError.textContent.trim() : '') || control.validationMessage;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${focusTarget.id || control.id}`;
      link.textContent = `${getFieldLabel(control)}: ${message}`;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        focusTarget.focus();
      });
      item.appendChild(link);
      list.appendChild(item);
    });
    summary.hidden = invalidFields.length === 0;
    if (focusFirst && invalidFields[0]) getFocusTarget(invalidFields[0]).focus();
  };

  document.addEventListener('invalid', (event) => {
    if (!event.target.matches?.('[data-zoria-personalization-input], .zoria-file-upload input[type="file"]')) return;
    event.preventDefault();
    queueMicrotask(() => updateErrorSummary(event.target.form, true));
  }, true);

  ['input', 'change'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (!event.target.matches?.('[data-zoria-personalization-input], .zoria-file-upload input[type="file"]')) return;
      queueMicrotask(() => updateErrorSummary(event.target.form));
    });
  });

  const revealImageUpload = (input, message) => {
    const field = input.closest('.zoria-file-upload');
    const error = field?.querySelector('.input-error-msg');
    const status = field?.querySelector('.zoria-file-upload__status');
    const picker = field?.querySelector('.zoria-file-upload__picker');

    input.setCustomValidity(message);
    input.setAttribute('aria-invalid', 'true');
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    if (status) status.textContent = message;
    field?.closest('details')?.setAttribute('open', '');
    field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    updateErrorSummary(input.form);
    requestAnimationFrame(() => {
      if (picker) {
        picker.focus({ preventScroll: true });
        return;
      }
      input.focus({ preventScroll: true });
    });
  };

  const getProductButtons = (form) => Array.from(document.querySelectorAll('.main-product-atc'))
    .filter((button) => button.form === form);

  const setButtonError = (form, message = '') => {
    getProductButtons(form).forEach((button) => {
      const error = button.querySelector('.main-atc__error, .sticky-atc__error');
      const label = button.querySelector('.main-atc__label, .sticky-atc__label');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
      error.classList.add('visually-hidden');
      error.style.removeProperty('display');
      if (label) {
        label.hidden = false;
        label.style.removeProperty('display');
      }
    });
  };

  const revealInvalidControl = (control) => {
    const field = control.closest('.custom-product-field');
    const error = field?.querySelector('.input-error-msg');
    const message = control.validationMessage || field?.dataset.atcErrorMsg || 'יש להשלים את השדה לפני ההוספה לסל';

    control.setAttribute('aria-invalid', 'true');
    control.classList.add('input--error');
    if (error) {
      error.textContent = message;
      error.hidden = false;
      error.setAttribute('data-visible', 'true');
    }
    field?.closest('details')?.setAttribute('open', '');
    field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    updateErrorSummary(control.form);
    requestAnimationFrame(() => getFocusTarget(control)?.focus({ preventScroll: true }));
  };

  const validatePersonalizationForm = (form) => {
    const invalidControl = getInvalidPersonalizationFields(form)[0];
    if (!invalidControl) {
      setButtonError(form);
      return true;
    }

    if (invalidControl.matches('.zoria-file-upload input[type="file"]')) {
      const message = invalidControl.files?.length > 0 && invalidControl.validationMessage
        ? invalidControl.validationMessage
        : invalidControl.files?.length > 0
          ? 'ממתינים לסיום בדיקת התמונה'
          : invalidControl.closest('.zoria-file-upload')?.dataset.requiredMessage || 'יש להעלות תמונה לפני ההוספה לסל';
      revealImageUpload(invalidControl, message);
      setButtonError(form, 'יש להעלות תמונה לפני ההוספה לסל');
      return false;
    }

    revealInvalidControl(invalidControl);
    setButtonError(form, 'יש להשלים את פרטי ההתאמה האישית');
    return false;
  };

  const syncButtonActionability = (button) => {
    const form = button.form;
    if (!form) return;
    const productAvailable = button.dataset.productAvailable === 'true';
    button.setAttribute('aria-disabled', String(!productAvailable));
    if (productAvailable && button.disabled) button.disabled = false;
    if (!productAvailable && !button.disabled) button.disabled = true;
  };

  const keepStickyButtonActionable = (button) => {
    if (button.dataset.zoriaActionableReady === 'true') return;
    button.dataset.zoriaActionableReady = 'true';
    const form = button.form;
    if (!form) return;

    const observer = new MutationObserver(() => syncButtonActionability(button));
    observer.observe(button, { attributes: true, attributeFilter: ['disabled'] });
    ['input', 'change'].forEach((eventName) => {
      form.addEventListener(eventName, () => queueMicrotask(() => {
        syncButtonActionability(button);
        if (getInvalidPersonalizationFields(form).length === 0) setButtonError(form);
      }));
    });
    syncButtonActionability(button);
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
    if (validatePersonalizationForm(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('[id^="SectionAtcBtn-"]')) return;
    const button = event.target.closest('[id^="SectionAtcBtn-"]');
    const form = button.form || document.querySelector('product-form[data-main="true"] form[action*="/cart/add"]');
    if (!form || validatePersonalizationForm(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  const initCompletionEvent = (form) => {
    if (!form || form.dataset.zoriaCompletionReady === 'true') return;
    form.dataset.zoriaCompletionReady = 'true';
    form.addEventListener('submit', () => {
      if (!form.checkValidity()) return;
      form.dataset.zoriaCompletionPending = 'true';
      clearTimeout(completionTimers.get(form));
      completionTimers.set(form, setTimeout(() => {
        delete form.dataset.zoriaCompletionPending;
      }, 15000));
    });
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'product-form') return;
      const pendingForms = document.querySelectorAll('form[data-zoria-completion-pending="true"]');
      if (pendingForms.length !== 1) return;
      const form = pendingForms[0];
      clearTimeout(completionTimers.get(form));
      delete form.dataset.zoriaCompletionPending;
      dispatch('personalization_completed', 'product');
    });
  }

  const initTextField = (field) => {
    if (field.dataset.zoriaReady === 'true') return;
    const input = field.querySelector('[data-zoria-personalization-input]');
    if (!input) return;

    field.dataset.zoriaReady = 'true';
    const counter = field.querySelector('[data-zoria-character-count]');
    const preview = field.querySelector('[data-zoria-engraving-preview]');
    const error = field.querySelector('.input-error-msg');
    const form = input.form;
    let started = false;
    let completedValue = '';
    initCompletionEvent(form);

    const update = (showError = true) => {
      if (counter) counter.textContent = input.value.length;
      if (preview) preview.textContent = input.value || 'הטקסט שלכם';
      if (!input.validity.valid) {
        if (!showError) {
          input.removeAttribute('aria-invalid');
          input.classList.remove('input--error');
          if (error) error.removeAttribute('data-visible');
          return;
        }
        input.setAttribute('aria-invalid', 'true');
        input.classList.add('input--error');
        if (error) {
          if (input.validationMessage) error.textContent = input.validationMessage;
          error.setAttribute('data-visible', 'true');
        }
        return;
      }
      input.removeAttribute('aria-invalid');
      input.classList.remove('input--error');
      if (error) error.removeAttribute('data-visible');
    };

    input.addEventListener('input', () => {
      if (!started && input.value.length > 0) {
        started = true;
        dispatch('personalization_started', 'engraving');
      }
      if (input.value.length === 0) completedValue = '';
      input.setCustomValidity('');
      if (input.maxLength > 0 && input.value.length > input.maxLength) {
        input.setCustomValidity(input.dataset.maxError || 'הטקסט חורג ממגבלת התווים');
      } else {
        input.setCustomValidity(getLanguageError(input.value, input.dataset.allowedLanguages));
      }
      update();
    });

    input.addEventListener('invalid', () => {
      input.setAttribute('aria-invalid', 'true');
      input.classList.add('input--error');
      if (error) error.setAttribute('data-visible', 'true');
    });

    input.addEventListener('blur', () => {
      if (input.value.length > 0 && input.validity.valid && input.value !== completedValue) {
        completedValue = input.value;
        dispatch('engraving_completed', 'engraving');
      }
    });

    if (form) {
      form.addEventListener('formdata', (event) => {
        if (input.disabled) {
          event.formData.delete(`properties[${field.dataset.name}]`);
          return;
        }
        event.formData.set(`properties[${field.dataset.name}]`, input.value);
      });
    }

    update(false);
  };

  const initFileField = (field) => {
    if (field.dataset.zoriaReady === 'true') return;
    const input = field.querySelector('input[type="file"]');
    if (!input) return;

    field.dataset.zoriaReady = 'true';
    const picker = field.querySelector('.zoria-file-upload__picker');
    const remove = field.querySelector('.zoria-file-upload__remove');
    const preview = field.querySelector('.zoria-file-upload__preview');
    const status = field.querySelector('.zoria-file-upload__status');
    const error = field.querySelector('.input-error-msg');
    initCompletionEvent(input.form);
    input.dataset.zoriaUploadState = input.files?.length ? 'complete' : 'idle';
    const requiredMessage = field.dataset.requiredMessage || 'יש לבחור תמונה';
    if (input.required && !input.files?.length) input.setCustomValidity(requiredMessage);
    const initialStatus = status.textContent.trim();
    const initialPicker = picker.textContent.trim();
    const replaceLabel = picker.dataset.replaceLabel || 'החלפת תמונה';
    const maxSize = Number(field.dataset.maxSize || 0);
    const showPreview = field.dataset.showPreview !== 'false';
    const minWidth = Number(field.dataset.minWidth || 0);
    const minHeight = Number(field.dataset.minHeight || 0);
    let previewUrl = '';
    const notifySummary = () => field.dispatchEvent(new CustomEvent('zoria:summary-change', { bubbles: true }));

    picker?.addEventListener('click', () => input.click());

    const clearPreview = () => {
      preview.onload = null;
      preview.onerror = null;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = '';
      preview.hidden = true;
      preview.removeAttribute('src');
    };

    const reset = () => {
      input.value = '';
      input.dataset.zoriaUploadState = 'idle';
      input.setCustomValidity(input.required ? requiredMessage : '');
      input.removeAttribute('aria-invalid');
      error.hidden = true;
      remove.hidden = true;
      picker.textContent = initialPicker;
      status.textContent = initialStatus;
      clearPreview();
      notifySummary();
    };

    const matchesAccept = (file) => {
      const rules = input.accept.split(',').map((rule) => rule.trim().toLowerCase()).filter(Boolean);
      if (rules.length === 0) return true;
      const extension = `.${file.name.split('.').pop().toLowerCase()}`;
      return rules.some((rule) => {
        if (rule.startsWith('.')) return extension === rule;
        if (rule.endsWith('/*')) return file.type.startsWith(rule.slice(0, -1));
        return file.type.toLowerCase() === rule;
      });
    };

    const fail = (message) => {
      input.value = '';
      input.dataset.zoriaUploadState = 'failed';
      input.setCustomValidity(message);
      input.setAttribute('aria-invalid', 'true');
      error.textContent = message;
      error.hidden = false;
      status.textContent = message;
      remove.hidden = true;
      picker.textContent = initialPicker;
      clearPreview();
      dispatch('image_upload_error', 'image');
      notifySummary();
      updateErrorSummary(input.form);
      (picker || input).focus();
    };

    input.addEventListener('change', () => {
      const file = input.files[0];
      if (!file) {
        reset();
        return;
      }
      input.dataset.zoriaUploadState = 'validating';
      dispatch('image_upload_started', 'image');
      if (!matchesAccept(file)) {
        fail(input.dataset.typeError || 'סוג הקובץ אינו נתמך');
        return;
      }
      if (maxSize > 0 && file.size > maxSize) {
        fail(input.dataset.sizeError || 'הקובץ גדול מהמותר');
        return;
      }

      input.setCustomValidity('ממתינים לסיום בדיקת התמונה');
      input.removeAttribute('aria-invalid');
      error.hidden = true;
      clearPreview();
      previewUrl = URL.createObjectURL(file);
      preview.onload = () => {
        if ((minWidth > 0 && preview.naturalWidth < minWidth) || (minHeight > 0 && preview.naturalHeight < minHeight)) {
          fail(field.dataset.resolutionError || 'רזולוציית התמונה נמוכה מדי');
          return;
        }
        preview.onload = null;
        preview.onerror = null;
        input.setCustomValidity('');
        input.dataset.zoriaUploadState = 'complete';
        input.removeAttribute('aria-invalid');
        error.hidden = true;
        status.textContent = file.name;
        remove.hidden = false;
        picker.textContent = replaceLabel;
        updateErrorSummary(input.form);
        dispatch('image_upload_completed', 'image');
        notifySummary();
      };
      preview.onerror = () => fail(input.dataset.typeError || 'לא הצלחנו לקרוא את התמונה. נסו קובץ אחר.');
      preview.src = previewUrl;
      preview.hidden = !showPreview;
      status.textContent = 'בודקים את התמונה…';
    });

    input.addEventListener('invalid', () => {
      const message = input.files.length > 0 && input.validationMessage
        ? input.validationMessage
        : requiredMessage;
      input.setCustomValidity(message);
      input.setAttribute('aria-invalid', 'true');
      error.textContent = message;
      error.hidden = false;
      status.textContent = message;
    });

    remove.addEventListener('click', () => {
      reset();
      updateErrorSummary(input.form);
      (picker || input).focus();
    });
  };

  const initOptionalPersonalization = (productInfo) => {
    const choice = productInfo.querySelector('[data-zoria-personalization-choice]');
    if (!choice || choice.dataset.zoriaReady === 'true') return;
    choice.dataset.zoriaReady = 'true';
    const form = productInfo.querySelector('form[action*="/cart/add"]');

    const update = () => {
      const enabled = choice.querySelector('input:checked')?.value === 'true';
      productInfo.dataset.personalizationActive = String(enabled);
      productInfo.querySelectorAll('.custom-product-field input, .custom-product-field select, .custom-product-field textarea')
        .forEach((control) => { control.disabled = !enabled; });
      if (enabled) dispatch('personalization_started', 'optional');
      updateErrorSummary(productInfo.querySelector('form'));
    };

    form?.addEventListener('formdata', (event) => {
      if (choice.querySelector('input:checked')?.value === 'true') return;
      productInfo.querySelectorAll('.custom-product-field').forEach((field) => {
        if (field.dataset.name) event.formData.delete(`properties[${field.dataset.name}]`);
        field.querySelectorAll('[name^="properties["]').forEach((control) => {
          event.formData.delete(control.name);
        });
      });
    });
    choice.addEventListener('change', update);
    update();
  };

  const appendSummaryRow = (list, label, value) => {
    const row = document.createElement('div');
    const term = document.createElement('dt');
    const description = document.createElement('dd');
    term.textContent = label;
    description.textContent = value;
    row.append(term, description);
    list.appendChild(row);
  };

  const getSummaryFieldValue = (field) => {
    const file = field.querySelector('input[type="file"]');
    if (file) return file.files?.length ? 'נבחרה תמונה' : 'טרם נבחרה תמונה';
    const singleOption = field.querySelector('.zoria-personalization-single-option');
    if (singleOption) return singleOption.textContent.trim();
    const select = field.querySelector('select');
    if (select) return select.selectedOptions?.[0]?.textContent.trim() || select.value;
    const checked = field.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;
    const control = field.querySelector('[data-zoria-personalization-input]');
    return control?.value.trim() || 'טרם הוזן';
  };

  const initSummary = (summary) => {
    if (summary.dataset.zoriaReady === 'true') return;
    const productInfo = summary.closest('product-info');
    const list = summary.querySelector('[data-zoria-summary-list]');
    if (!productInfo || !list) return;
    summary.dataset.zoriaReady = 'true';

    const update = () => {
      const optionalInactive = productInfo.dataset.personalizationOptional === 'true'
        && productInfo.dataset.personalizationActive !== 'true';
      summary.hidden = optionalInactive;
      if (optionalInactive) return;

      list.replaceChildren();
      appendSummaryRow(list, 'מוצר', list.dataset.productTitle);

      const variantGroups = productInfo.querySelectorAll('variant-selects .product-form__input');
      variantGroups.forEach((group) => {
        const label = group.querySelector('.form__label')?.textContent.trim();
        const select = group.querySelector('select[name^="options["]');
        const radio = group.querySelector('input[name^="options["]:checked');
        const value = select?.selectedOptions?.[0]?.textContent.trim() || radio?.value;
        if (label && value) appendSummaryRow(list, label, value);
      });
      if (variantGroups.length === 0 && list.dataset.hasOnlyDefaultVariant !== 'true') {
        appendSummaryRow(list, 'בחירה', list.dataset.initialVariant);
      }

      productInfo.querySelectorAll('.custom-product-field:not(.zoria-confirmation-field)').forEach((field) => {
        const label = field.querySelector('.form__label')?.textContent.trim();
        if (label) appendSummaryRow(list, label, getSummaryFieldValue(field));
      });

      const priceRoot = productInfo.querySelector('.product-page-price');
      const price = priceRoot?.querySelector('.price__sale .price-item--sale')
        || priceRoot?.querySelector('.price__regular .price-item--regular, .price-item--regular');
      appendSummaryRow(list, 'מחיר סופי', price?.textContent.trim() || list.dataset.initialPrice);
    };

    ['input', 'change', 'zoria:summary-change'].forEach((eventName) => {
      productInfo.addEventListener(eventName, () => queueMicrotask(update));
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      let unsubscribe;
      unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (!summary.isConnected) {
          unsubscribe?.();
          return;
        }
        if (event?.data?.sectionId === productInfo.dataset.section) update();
      });
    }
    update();
  };

  const init = (root = document) => {
    root.querySelectorAll('custom-product-field').forEach(initTextField);
    root.querySelectorAll('.zoria-initial-field select').forEach((control) => {
      if (control.dataset.zoriaReady === 'true') return;
      control.dataset.zoriaReady = 'true';
      initCompletionEvent(control.form);
      control.addEventListener('change', () => {
        if (!control.validity.valid) return;
        control.removeAttribute('aria-invalid');
        control.classList.remove('input--error');
        const error = control.closest('.custom-product-field')?.querySelector('.input-error-msg');
        if (error) {
          error.hidden = true;
          error.removeAttribute('data-visible');
        }
        updateErrorSummary(control.form);
        if (getInvalidPersonalizationFields(control.form).length === 0) setButtonError(control.form);
      });
    });
    root.querySelectorAll('.zoria-file-upload').forEach(initFileField);
    root.querySelectorAll('product-info[data-personalization-optional="true"]').forEach(initOptionalPersonalization);
    root.querySelectorAll('[data-zoria-personalization-summary]').forEach(initSummary);
    root.querySelectorAll('.main-product-atc[data-product-available]').forEach(keepStickyButtonActionable);
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const available = event?.data?.variant?.available === true;
      document.querySelectorAll('.main-product-atc[data-product-available]').forEach((button) => {
        button.dataset.productAvailable = String(available);
        syncButtonActionability(button);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
